import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Plus, Users, Hourglass, ChevronRight, Eye } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { DemoBadge, EmptyState, PageHeader } from "@/components/Bits";

const TYPE_LABEL = { PRIVATE: "Entre amis", COMMUNITY: "Community", BUSINESS: "Business", PARTNER: "Partner", EXPERIENCE: "Experience" };
import { LeagueIcon } from "@/components/LeagueIcon";
import { enterTenant, getTenant } from "@/lib/tenant";

function DeletedLeagues() {
  const { loadLeagues } = useAuth();
  const [list, setList] = useState([]);
  const load = () => api.get("/me/deleted-leagues").then(({ data }) => setList(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!list.length) return null;
  const restore = async (id) => { try { await api.post(`/leagues/${id}/restore`); toast.success("Ligue restaurée"); load(); loadLeagues?.(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <section className="mt-8" data-testid="deleted-leagues">
      <h2 className="mb-2 text-sm font-bold text-slate-300">Ligues supprimées (récupérables)</h2>
      {list.map((l) => (
        <div key={l.id} className="mpl-card mb-2 flex items-center justify-between gap-3 p-3 text-sm">
          <span className="min-w-0 [overflow-wrap:anywhere]">{l.name} <span className="text-xs text-slate-500">· effacée définitivement le {new Date(l.purge_after).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</span></span>
          <button onClick={() => restore(l.id)} className="mpl-btn-ghost text-xs" data-testid={`restore-league-${l.id}`}>Restaurer</button>
        </div>
      ))}
    </section>
  );
}

export default function Leagues() {
  const { leagues } = useAuth();
  return (
    <div>
      <PageHeader eyebrow="Toutes tes ligues" title="Mes ligues"
        action={<Link to="/ligues/nouvelle" className="mpl-btn-primary shrink-0" data-testid="create-league-button"><Plus size={16} /> Créer une ligue</Link>} />
      {leagues.length ? (
        <div className="grid grid-cols-1 gap-3" data-testid="leagues-list">
          {leagues.map((l) => (
            <Link key={l.id} to={`/ligues/${l.id}`} data-testid={`league-item-${l.id}`} className="mpl-card mpl-rise flex min-w-0 items-center gap-4 p-4 transition-colors hover:bg-[#1A2542]">
              <LeagueIcon l={l} testId={`league-icon-${l.id}`} />
              <div className="min-w-0 flex-1">
                <p className="flex min-w-0 items-center gap-1.5 font-bold"><span className="line-clamp-2 min-w-0 leading-snug [overflow-wrap:anywhere]" data-testid={`league-name-${l.id}`}>{l.name}</span><VerifiedBadge show={l.brand?.verified} size={16} testId={`league-verified-${l.id}`} /></p>{(l.is_demo || l.archived) && <p className="flex flex-wrap items-center gap-2">{l.is_demo && <DemoBadge />} {l.archived && <span className="rounded-full border border-white/15 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-300" data-testid={`league-archived-${l.id}`}>Archivée</span>}</p>}
                <p className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-400">
                  <span className="rounded-full border border-white/15 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-300" data-testid={`league-type-${l.id}`}>{TYPE_LABEL[l.league_type] || "Entre amis"}</span>
                  <span className="flex items-center gap-1"><Users size={12} /> {l.member_count}{l.max_members ? `/${l.max_members}` : ""}</span>
                  <span>{l.mode === "custom" ? "Personnalisée" : "Classique"}</span>
                  {l.my_role === "owner" && <span className="text-[#B7F52A]">Propriétaire</span>}
                  {!l.competition_started && <span className="flex items-center gap-1 text-amber-300"><Hourglass size={11} /> {l.status_label}</span>}
                  {l.league_type === "BUSINESS" && !getTenant() && <button type="button" onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); enterTenant(l.id); }} className="rounded-full border border-[#B7F52A]/40 px-2 py-0.5 text-[10px] font-bold text-[#B7F52A]" data-testid={`league-open-tenant-${l.id}`}>Ouvrir l'espace entreprise</button>}
                </p>
              </div>
              <span className="text-right"><span className="block font-mono-num text-lg font-bold text-[#B7F52A]">{l.my_points}</span><span className="text-[10px] text-slate-500">pts</span></span>
              <ChevronRight size={18} className="text-slate-500" />
            </Link>
          ))}
        </div>
      ) : (
        <EmptyState icon={Users} title="Vous n'avez pas encore de ligue" text="Créez la vôtre et invitez jusqu'à 19 amis, ou ouvrez un lien d'invitation." cta="Créer une ligue" to="/ligues/nouvelle" testId="leagues-empty" />
      )}
      <ObservedLeagues />
      <DeletedLeagues />
    </div>
  );
}

function ObservedLeagues() {
  const { observed } = useAuth();
  if (!observed.length) return null;
  return (
    <section className="mt-8" data-testid="observed-leagues">
      <h2 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-sky-200"><Eye size={15} /> Ligues suivies en observateur</h2>
      <div className="grid grid-cols-1 gap-3">
        {observed.map((l) => (
          <Link key={l.id} to={`/ligues/${l.id}`} data-testid={`observed-league-${l.id}`} className="mpl-card flex min-w-0 items-center gap-4 p-4 transition-colors hover:bg-[#1A2542]">
            {l.icon_url ? <LeagueIcon l={l} testId={`observed-league-icon-${l.id}`} /> : <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-sky-400/10 text-sky-200"><Eye size={20} /></span>}
            <div className="min-w-0 flex-1">
              <p className="flex min-w-0 items-center gap-1.5 font-bold"><span className="line-clamp-2 min-w-0 leading-snug [overflow-wrap:anywhere]" data-testid={`observed-league-name-${l.id}`}>{l.name}</span><VerifiedBadge show={l.brand?.verified} size={16} testId={`observed-league-verified-${l.id}`} /></p>
              <p className="mt-0.5 flex items-center gap-2 text-xs text-slate-400"><span className="flex items-center gap-1"><Users size={12} /> {l.member_count}{l.max_members ? `/${l.max_members}` : ""}</span><span className="text-sky-200">Observateur</span></p>
            </div>
            <ChevronRight size={18} className="text-slate-500" />
          </Link>
        ))}
      </div>
    </section>
  );
}
